import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getLandingByUrl, incrementLandingViews } from "../lib/database";
import { trackEvent } from "../lib/analytics";
import {
  getInitialLocale,
  persistLocale,
  updateUrlLocale,
  type SupportedLocale,
} from "../lib/localization";
import {
  absoluteUrl,
  applyRobots,
  hreflangUrl,
  setLinkRel,
  setMetaByName,
  setMetaByProperty,
  siteOrigin,
} from "../lib/seo";
import { LandingPagePreview } from "./LandingPagePreview";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { SITE_LOGO } from "./public/SiteNav";
import type { LandingWithBlocks } from "../hooks/useLandings";

export function PublicLandingPage() {
  const { slug = "" } = useParams();
  const [locale, setLocale] = useState<SupportedLocale>(() => getInitialLocale());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [landing, setLanding] = useState<LandingWithBlocks | null>(null);
  const tr = (en: string, es: string) => (locale === "es" ? es : en);

  useEffect(() => {
    updateUrlLocale(locale);
    persistLocale(locale);
    document.documentElement.lang = locale;
  }, [locale]);

  useEffect(() => {
    const load = async () => {
      if (!slug) {
        setError(locale === "es" ? "No se proporcionó la URL de la landing" : "Landing URL not provided");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const data = (await getLandingByUrl(slug)) as LandingWithBlocks;
        if (!data || !data.is_public || data.status !== "Published") {
          setError(locale === "es" ? "Landing no disponible" : "Landing not available");
          setLanding(null);
          return;
        }

        setLanding(data);
        trackEvent("landing_view", { slug: data.url, locale });
        await incrementLandingViews(data.id, data.views ?? 0);
      } catch (err: any) {
        setError(err?.message || (locale === "es" ? "No se pudo cargar la landing" : "Failed to load landing"));
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [slug, locale]);

  useEffect(() => {
    if (!landing) return;

    const seoMetadata = landing.seo_metadata || {};
    const localizedSeo = (seoMetadata as any)[locale] || seoMetadata;
    const origin = siteOrigin(localizedSeo.baseUrl);
    const title = localizedSeo.metaTitle || landing.name;
    const description =
      localizedSeo.metaDescription ||
      (locale === "es"
        ? "Landing de campaña de ON45D Studio enfocada en conversión de cotizaciones."
        : "ON45D Studio campaign landing page focused on quote conversion.");
    const canonicalUrl =
      localizedSeo.canonicalUrl || hreflangUrl(origin, window.location.pathname, locale);
    const robotsIndex = localizedSeo.robotsIndex !== undefined ? localizedSeo.robotsIndex : true;
    const robotsFollow = localizedSeo.robotsFollow !== undefined ? localizedSeo.robotsFollow : true;
    const ogImage = localizedSeo.ogImage || localizedSeo.og_image;

    document.title = title;
    setMetaByName("description", description);
    applyRobots(Boolean(robotsIndex), Boolean(robotsFollow));
    setLinkRel("canonical", canonicalUrl);
    setLinkRel("alternate", hreflangUrl(origin, window.location.pathname, "es"), { hreflang: "es" });
    setLinkRel("alternate", hreflangUrl(origin, window.location.pathname, "en"), { hreflang: "en" });
    setLinkRel("alternate", hreflangUrl(origin, window.location.pathname, "en"), { hreflang: "x-default" });
    setMetaByProperty("og:type", "website");
    setMetaByProperty("og:title", localizedSeo.ogTitle || title);
    setMetaByProperty("og:description", localizedSeo.ogDescription || description);
    setMetaByProperty("og:url", canonicalUrl);
    if (ogImage) setMetaByProperty("og:image", absoluteUrl(ogImage, origin));
    setMetaByName("twitter:card", "summary");
    setMetaByName("twitter:title", localizedSeo.ogTitle || title);
    setMetaByName("twitter:description", localizedSeo.ogDescription || description);
  }, [landing, locale]);

  if (loading) {
    return (
      <div className="public-site flex min-h-screen items-center justify-center">
        <p className="text-[#B8CFCF]">{tr("Loading landing...", "Cargando landing...")}</p>
      </div>
    );
  }

  if (error || !landing) {
    return (
      <div className="public-site flex min-h-screen flex-col items-center justify-center gap-6 p-6">
        <img src={SITE_LOGO} alt="ON45D Studio" className="h-[34px] w-auto" />
        <div className="rounded-2xl border border-white/10 bg-[#0F1A1A] p-6 text-center">
          <h1 className="text-lg font-bold text-white">{tr("Landing unavailable", "Landing no disponible")}</h1>
          <p className="mt-2 text-sm text-[#B8CFCF]">{error || tr("Not found", "No encontrada")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0F0F]">
      {/* Bottom corner so it never covers the header CTA */}
      <div className="fixed bottom-4 right-4 z-[120]">
        <LanguageSwitcher locale={locale} onChange={setLocale} />
      </div>
      <LandingPagePreview landing={landing} locale={locale} />
    </div>
  );
}
